import { useCallback, useEffect, useState } from 'react';

import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import dealPageService from '~/services/cruises/DealPageService';

import { parseSearchString } from '~/utils/url';

import { CruiseDealPageBase, CruiseDealPageListQuery } from '../pages/DealsPage/types';

interface Filter {
  name?: string;
  cruiseLine?: string;
  rateCodes?: string;
}

interface CruisesData {
  dealsPage: Array<CruiseDealPageBase>;
  page: number;
  total: number;
  isLoading: boolean;
  recordsPerPage: number;
  paginate: (page: number) => void;
  setFilter: (value: Filter) => void;
  filter: Filter;
  getDealsPage: (params?: CruiseDealPageListQuery) => Promise<void>;
}

const RECORDS_PER_PAGE = 15;

export interface Props {
  isActive?: boolean;
  recordsPerPage?: number;
}

export default function useCruiseDealsPage({ isActive, recordsPerPage: customRecordsPerPage }: Props): CruisesData {
  const [filter, setFilter] = useState<Filter>({});
  const { search } = useLocation();
  const { push: setQueryString } = useHistory();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dealsPage, setDealsPage] = useState<Array<CruiseDealPageBase>>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getDealsPage = useCallback(
    async (params?: CruiseDealPageListQuery) => {
      try {
        const recordsPerPage = params?.take || customRecordsPerPage || RECORDS_PER_PAGE;
        const skip = (page - 1) * recordsPerPage;
        setIsLoading(true);
        const uniqueParams = {
          ...(params || {}),
          ...(isActive ? { isActive: true } : {}),
        };
        const res = await dealPageService.listWithPagination({
          ...uniqueParams,
          skip,
          take: recordsPerPage,
        });
        setDealsPage(res.result);
        setTotal(res.total);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    },
    [isActive, page, customRecordsPerPage],
  );

  const setCurrentPage = useCallback((): void => {
    const queryString = parseSearchString(search);
    if (queryString) {
      const pageToGo = parseInt(queryString.page as string);
      if (pageToGo) {
        setPage(pageToGo);
      }
    }
  }, [search]);

  const paginate = useCallback(
    (page: number): void => {
      const queryString = new URLSearchParams({
        page: page.toString(),
      }).toString();
      setQueryString({ search: queryString });
      setPage(page);
    },
    [setQueryString],
  );

  useEffect(() => {
    setCurrentPage();
  }, [setCurrentPage]);

  useEffect(() => {
    getDealsPage();
  }, [getDealsPage]);

  return {
    filter,
    setFilter,
    dealsPage,
    getDealsPage,
    page,
    recordsPerPage: customRecordsPerPage || RECORDS_PER_PAGE,
    paginate,
    total,
    isLoading,
  };
}
